import { combineClasses } from '~/util'
import { DESTINATION_TYPES } from '~/store'
import {
  SubTitle,
  MiniTitle,
  UnitCard,
  AmenityCard,
  EntranceCard,
} from '~/components'

import styles from './TourSteps.module.scss'
import {
  Amenity,
  Floor,
  FloorPlate,
  ID,
  isAmenityMarker,
  isUnitMarker,
  LockDevice,
  Marker,
  TourBuildings,
  Unit,
} from '~/service'
import { Nilable } from 'tsdef'

function formatName(prefix: string, name: number | string) {
  // Default to the name received from the API.
  // If the name is a number or is not passed,
  // generate a name from the number.
  return name && isNaN(Number(name)) ? name : `${prefix} ${name}`
}

interface UnitStepProps {
  marker: Marker<Unit>
  url: string
  hideStepLabels: boolean
  showUnitPrices: boolean
  showThumbnails: boolean
  showModelPrices: boolean
  showUntourableUnits: boolean
  lock: LockDevice | null | undefined
  onLockError: (label: string, error: unknown) => void
}

export function UnitStep({
  marker,
  url,
  hideStepLabels,
  showUnitPrices,
  showUntourableUnits,
  showModelPrices,
  lock,
  onLockError,
}: UnitStepProps) {
  return (
    <UnitCard
      data-testid="marker"
      data-type="unit"
      href={url}
      className={styles.UnitCard}
      unit={marker.item}
      title={marker.title}
      label={!hideStepLabels ? marker.badge ?? marker.label : null}
      showUnitPrices={showUnitPrices}
      showUntourableUnits={showUntourableUnits}
      showModelPrices={showModelPrices}
      lock={lock}
      onLockError={onLockError}
      onToggleFavorite={null}
    />
  )
}

interface AmenityStepProps {
  marker: Marker<Amenity>
  url: string
  hideStepLabels: boolean
  showThumbnails: boolean
  lock: LockDevice | null | undefined
  onLockError: (label: string, error: unknown) => void
}

export function AmenityStep({
  marker,
  url,
  hideStepLabels,
  showThumbnails,
  lock,
  onLockError,
}: AmenityStepProps) {
  return (
    <AmenityCard
      data-testid="marker"
      data-type="amenity"
      href={url}
      className={styles.AmenityCard}
      label={!hideStepLabels ? marker.label : undefined}
      name={marker.item.name}
      imageUrl={showThumbnails ? marker.item.imageUrl : undefined}
      description={marker.item.description}
      lock={lock}
      onLockError={onLockError}
    />
  )
}

interface SharedProps {
  /**
   * Whether or not to show pricing on units.
   */
  showUnitPrices: boolean
  /**
   * Whether or not to show pricing on model units.
   */
  showModelPrices: boolean
  /**
   * Whether or not to show units that are unavailable to tour.
   */
  showUntourableUnits: boolean
  getUnitURL: (id: ID) => string
  getAmenityURL: (id: ID) => string
  hideStepLabels: boolean
  stickyStepHeaderClassName?: string
  showThumbnails: boolean
  lockDeviceAccesses: Nilable<LockDevice[]>
  onLockError: (name: string, error: unknown) => void
}

type TourFloorProps = SharedProps &
  React.HTMLAttributes<HTMLDivElement> & {
    name?: Nilable<string>
    floorPlate?: FloorPlate
    markers: Marker<Unit | Amenity>[]
    entrances?: Marker<any>[]
  }

/**
 * Show the user's tour steps for a single floor.
 */
export function TourFloor({
  floorPlate,
  name = floorPlate?.name,
  markers,
  entrances,
  onLockError,
  lockDeviceAccesses,
  showUnitPrices,
  showModelPrices,
  showUntourableUnits,
  getUnitURL,
  getAmenityURL,
  hideStepLabels,
  className,
  stickyStepHeaderClassName,
  showThumbnails,
}: TourFloorProps) {
  return (
    <div
      className={combineClasses(styles.TourFloor, className)}
      data-testid="TourFloor"
    >
      {name && (
        <div
          className={combineClasses(
            styles.floorHeader,
            stickyStepHeaderClassName
          )}
        >
          <SubTitle data-testid="floorTitle" className={styles.floorTitle}>
            {formatName('Floor', name)}
          </SubTitle>
        </div>
      )}
      <div className={styles.floorContent}>
        {!!entrances?.length && (
          <EntranceCard
            data-testid="entrance"
            transparent
            className={styles.EntranceCard}
            name={entrances[0].item.name}
            lock={lockDeviceAccesses?.find(
              (d) => d.id === entrances[0].item.lockDeviceId
            )}
            onLockError={onLockError}
          />
        )}
        {markers.map((marker, i) => {
          if (isUnitMarker(marker)) {
            return (
              <UnitStep
                url={getUnitURL(marker.item.id)}
                key={i}
                marker={marker}
                hideStepLabels={hideStepLabels}
                showUnitPrices={showUnitPrices}
                showUntourableUnits={showUntourableUnits}
                showThumbnails={showThumbnails}
                showModelPrices={showModelPrices}
                onLockError={onLockError}
                lock={lockDeviceAccesses?.find(
                  (d) => d.id === marker.item.lockDeviceId
                )}
              />
            )
          } else if (isAmenityMarker(marker)) {
            return (
              <AmenityStep
                url={getAmenityURL(marker.item.id)}
                key={i}
                marker={marker}
                hideStepLabels={hideStepLabels}
                showThumbnails={showThumbnails}
                lock={lockDeviceAccesses?.find(
                  (d) => d.id === marker.item.lockDeviceId
                )}
                onLockError={onLockError}
              />
            )
          }

          return null
        })}
      </div>
    </div>
  )
}

interface FloorListProps extends SharedProps {
  floors: Floor[]
}

/**
 * Show the steps on a user's tour grouped by floor.
 */
export function FloorList({
  floors,
  onLockError,
  lockDeviceAccesses,
  getUnitURL,
  getAmenityURL,
  showUnitPrices,
  hideStepLabels,
  stickyStepHeaderClassName,
  showThumbnails,
  showModelPrices,
  showUntourableUnits,
}: FloorListProps) {
  return (
    <>
      {floors.map((floor, i) => {
        // Break markers into leasing office markers
        // non-leasing office markers.
        const groups = floor.markers.reduce(
          (acc, curr) => {
            if (curr.type === DESTINATION_TYPES.ENTRANCE)
              acc.entrances.push(curr as Marker<Amenity>)
            else acc.markers.push(curr)
            return acc
          },
          {
            markers: [] as Marker<Unit | Amenity>[],
            entrances: [] as Marker<Amenity>[],
          }
        )

        if (groups.markers.length > 0 || groups.entrances.length > 0) {
          return (
            <TourFloor
              {...floor}
              key={i}
              markers={groups.markers}
              onLockError={onLockError}
              lockDeviceAccesses={lockDeviceAccesses}
              entrances={groups.entrances}
              getUnitURL={getUnitURL}
              getAmenityURL={getAmenityURL}
              showUnitPrices={showUnitPrices}
              hideStepLabels={hideStepLabels}
              stickyStepHeaderClassName={stickyStepHeaderClassName}
              showThumbnails={showThumbnails}
              showModelPrices={showModelPrices}
              showUntourableUnits={showUntourableUnits}
            />
          )
        } else {
          return null
        }
      })}
    </>
  )
}

interface TourBuildingProps extends SharedProps {
  building: {
    name: string
    floors: Floor[]
  }
}

/**
 * Show the steps on a user's tour for a single building.
 */
export function TourBuilding({
  building,
  onLockError,
  lockDeviceAccesses,
  getUnitURL,
  getAmenityURL,
  showUnitPrices,
  hideStepLabels,
  stickyStepHeaderClassName,
  showThumbnails,
  showModelPrices,
  showUntourableUnits,
}: TourBuildingProps) {
  return (
    <div className={styles.TourBuilding} data-testid="TourBuilding">
      <div
        className={combineClasses(
          styles.buildingHeader,
          stickyStepHeaderClassName
        )}
      >
        <MiniTitle className={styles.buildingTitleLabel}>Building</MiniTitle>
        <SubTitle data-testid="buildingTitle" className={styles.buildingTitle}>
          {building.name}
        </SubTitle>
      </div>
      <div className={styles.floorList}>
        <FloorList
          floors={building.floors}
          onLockError={onLockError}
          lockDeviceAccesses={lockDeviceAccesses}
          getUnitURL={getUnitURL}
          getAmenityURL={getAmenityURL}
          showUnitPrices={showUnitPrices}
          hideStepLabels={hideStepLabels}
          stickyStepHeaderClassName={stickyStepHeaderClassName}
          showThumbnails={showThumbnails}
          showModelPrices={showModelPrices}
          showUntourableUnits={showUntourableUnits}
        />
      </div>
    </div>
  )
}

interface BuildingListProps extends SharedProps {
  buildings: {
    name: string
    floors: Floor[]
  }[]
}

/**
 * Show the steps on a user's tour grouped by building.
 */
export function BuildingList({
  buildings,
  onLockError,
  lockDeviceAccesses,
  getUnitURL,
  getAmenityURL,
  showUnitPrices,
  hideStepLabels,
  stickyStepHeaderClassName,
  showThumbnails,
  showModelPrices,
  showUntourableUnits,
}: BuildingListProps) {
  return (
    <>
      {buildings.map((building, i) => (
        <TourBuilding
          key={i}
          building={building}
          onLockError={onLockError}
          lockDeviceAccesses={lockDeviceAccesses}
          getUnitURL={getUnitURL}
          getAmenityURL={getAmenityURL}
          showUnitPrices={showUnitPrices}
          hideStepLabels={hideStepLabels}
          showThumbnails={showThumbnails}
          stickyStepHeaderClassName={stickyStepHeaderClassName}
          showModelPrices={showModelPrices}
          showUntourableUnits={showUntourableUnits}
        />
      ))}
    </>
  )
}

type TourStepsProps = SharedProps &
  React.HTMLAttributes<HTMLDivElement> & {
    stops: TourBuildings
    includeUngrouped: boolean
  }

/**
 * Shows the list of steps on the user's tour grouped by building and floors or
 * as a flat list if everything is on a single floor of a single building.
 */
export function TourSteps({
  stops,
  onLockError,
  lockDeviceAccesses,
  showUnitPrices,
  getAmenityURL,
  getUnitURL,
  hideStepLabels,
  className,
  stickyStepHeaderClassName,
  includeUngrouped,
  showThumbnails,
  showModelPrices,
  showUntourableUnits,
  ...rest
}: TourStepsProps) {
  return (
    <div
      data-testid="TourSteps"
      className={combineClasses(styles.TourSteps, className)}
      {...rest}
    >
      {stops?.buildings && stops.buildings.length > 1 && (
        <BuildingList
          buildings={stops.buildings}
          onLockError={onLockError}
          lockDeviceAccesses={lockDeviceAccesses}
          getUnitURL={getUnitURL}
          getAmenityURL={getAmenityURL}
          showUnitPrices={showUnitPrices}
          hideStepLabels={hideStepLabels}
          stickyStepHeaderClassName={stickyStepHeaderClassName}
          showThumbnails={showThumbnails}
          showModelPrices={showModelPrices}
          showUntourableUnits={showUntourableUnits}
        />
      )}
      {stops?.buildings && stops.buildings.length === 1 && (
        <FloorList
          // className={styles.noBuildings}
          floors={stops.buildings[0].floors}
          onLockError={onLockError}
          lockDeviceAccesses={lockDeviceAccesses}
          getUnitURL={getUnitURL}
          getAmenityURL={getAmenityURL}
          showUnitPrices={showUnitPrices}
          hideStepLabels={hideStepLabels}
          showThumbnails={showThumbnails}
          showModelPrices={showModelPrices}
          showUntourableUnits={showUntourableUnits}
        />
      )}
      {includeUngrouped && stops?.ungrouped?.markers?.length > 0 && (
        <TourFloor
          className={styles.noBuildings}
          name={
            stops.markers.length > stops.ungrouped.markers.length
              ? 'More Amenities'
              : null
          }
          markers={stops.ungrouped.markers}
          onLockError={onLockError}
          lockDeviceAccesses={lockDeviceAccesses}
          getUnitURL={getUnitURL}
          getAmenityURL={getAmenityURL}
          showUnitPrices={showUnitPrices}
          hideStepLabels={hideStepLabels}
          showThumbnails={showThumbnails}
          showModelPrices={showModelPrices}
          showUntourableUnits={showUntourableUnits}
        />
      )}
    </div>
  )
}
